import { useState, useEffect, useContext } from 'src/ReactTS';
import * as AWS from 'aws-sdk';
import { ServiceContext } from '../provider/Service.provider';
import { IAuthInterface } from '../interface/user/login/IAuthInterface';
import { UserTypeEnum } from '../enum/UserTypeEnum';
import AuthPermissions from './AuthPermissions';
import { IUserSecurityDto } from '../interface/user/IUserSecurityDto';
import { IUserSecurityOfficerDto } from '../interface/user/officer/IUserSecurityOfficerDto';
import ConfigService from '../config/config.service';
import { AdminCreateUserRequest, AdminSetUserPasswordRequest, AdminUpdateUserAttributesRequest } from 'aws-sdk/clients/cognitoidentityserviceprovider';
import { RecaptchaToken } from 'src/util/Recaptcha';
import { SessionStorageEnum } from '../enum/SessionStorageEnum';
import { AuthError } from '../interface/Auth/AuthError';
import { Cache } from '@aws-amplify/cache';
import { signUp, confirmSignUp, signOut, signIn, confirmSignIn, resetPassword, resendSignUpCode, confirmResetPassword, getCurrentUser } from '@aws-amplify/auth';
import { useIdTokenJwt } from '../interface/generic-form/IAuthSession';

export function useAuthProvider(): IAuthInterface {
  const { userAuthService } = useContext(ServiceContext);
  const defaultTemporaryPassword = "Alterar@Preca123";
  const { idToken, expiration } = useIdTokenJwt();

  const [
    currentEmail,
    setCurrentEmail
  ] = useState<string | null | undefined>(undefined);
  const [
    currentUser,
    setCurrentUser
  ] = useState<any>(undefined);


  const setUserSession = async (mfaCode: string): Promise<void> => {
    try {
      Cache.setItem("jwt", null)

      const response = mfaCode ? (await userAuthService.signInWithMFA(await RecaptchaToken(), mfaCode)) : (await userAuthService.signIn(await RecaptchaToken()));

      if (response.user.type !== UserTypeEnum.Administrator) {
        throw new Error(`Não é permitido acessar o portal administrativo com acesso do tipo ${response.user.type}`)
      }

      if (response.user.active === false) {
        throw new Error('Usuário Inativo');
      }

      if (response.isMFAValidated === false) {
        throw new Error('VALIDATE_MFA');
      }

      setCurrentEmail(response.user.login);
      setUserPermissions(response.user);
      setCurrentUser(response.user);

      Cache.setItem("jwt", {
        token: idToken,
        expiration: expiration
      })

      sessionStorage.setItem(SessionStorageEnum.session, response.sessionToken)
    } catch (err) {
      throw err;
    }
  }

  const provider: IAuthInterface = {
    signUp: async (email: string, password: string) => {
      return signUp({
        username: email.toLowerCase().trim(),
        password,
        options: {
          userAttributes: {
            email: email.toLowerCase().trim(),
          },
        },
      });
    },

    confirmSignUp: async (email: string, code: string) => {
      return new Promise<any>(async (res, reject) => {
        try {
          res(await confirmSignUp({ username: email.toLowerCase().trim(), confirmationCode: code }));
        } catch (e) {
          const error = e as AuthError;
          if (error.code === 'UnexpectedLambdaException' || error.code === 'NotAuthorizedException') {
            res(true);
          } else {
            reject(error);
          }
        }
      });
    },

    signIn: async (email: string, password: string) => {
      try {
        const currentUser = await getCurrentUser().catch(() => null);
        if (currentUser) {
          await signOut();
        }

        Cache.setItem('jwt', null);
        sessionStorage.removeItem(SessionStorageEnum.session);

        const result = await signIn({
          username: email.toLowerCase().trim(),
          password,
          options: {
            clientMetadata: {
              "grecaptcha": await RecaptchaToken()
            }
          }
        });

        if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
          throw result;
        }

        await setUserSession(null)
      } catch (error) {
        throw error;
      }
    },

    signInWithMFA: async (email: string, password: string, mfaCode: string) => {
      try {
        const currentUser = await getCurrentUser().catch(() => null);
        if (currentUser) {
          await signOut();
        }

        Cache.setItem('jwt', null);
        sessionStorage.removeItem(SessionStorageEnum.session);

        const result = await signIn({
          username: email.toLowerCase().trim(),
          password,
          options: {
            clientMetadata: {
              "grecaptcha": await RecaptchaToken()
            }
          }
        });

        if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
          const recaptchaToken = await RecaptchaToken();
          const confirmed = await confirmSignIn({ challengeResponse: recaptchaToken });

          if (confirmed.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE') {
            await confirmSignIn({ challengeResponse: mfaCode });
          }
        } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE') {
          await confirmSignIn({ challengeResponse: mfaCode });
        }

        await setUserSession(mfaCode);
      } catch (error) {
        throw error;
      }
    },

    signOut: async () => {
      Cache.setItem('jwt', null);

      await userAuthService.signOut();
      await signOut();

      setCurrentUser(null);
      setCurrentEmail(null);
    },

    completeNewPassword: async (user: any, newPass: string) => {
      return confirmSignIn({ challengeResponse: newPass });
    },

    requestEmailConfirmation: async (email: string) => {
      return resendSignUpCode({ username: email.toLowerCase().trim() });
    },

    forgotPassword: async (email: string) => {
      return resetPassword({ username: email.toLowerCase().trim() });
    },

    forgotPasswordSubmit: async (email: string, code: string, newPassword: string) => {
      return confirmResetPassword({
        username: email.toLowerCase().trim(),
        confirmationCode: code,
        newPassword
      });
    },

    createUserCognito: (email: string, profile: string, setDefaultPassword: boolean) => {
      return new Promise<void>((resolve, reject) => {
        AWS.config.region = ConfigService.getConfig().awsConfig.cognitoRegion;
        AWS.config.credentials = new AWS.CognitoIdentityCredentials({
          IdentityPoolId: ConfigService.getConfig().awsConfig.userPoolIdentityPoolId
        });

        (AWS.config.credentials as AWS.CognitoIdentityCredentials).refresh(function (error) {
          if (error) {
            console.log(error);
          } else {
            console.log('REFRESHED');
          }
        });
        const cognitoidentityserviceprovider = new AWS.CognitoIdentityServiceProvider();
        cognitoidentityserviceprovider.config.region = ConfigService.getConfig().awsConfig.cognitoRegion;

        if (cognitoidentityserviceprovider.config.credentials) {
          cognitoidentityserviceprovider.config.credentials.accessKeyId = ConfigService.getConfig().awsConfig.userPoolWebClientId;
          cognitoidentityserviceprovider.config.credentials.secretAccessKey = '';
        }

        const params: AdminCreateUserRequest = {
          UserPoolId: ConfigService.getConfig().awsConfig.cognitoUserPoolId,
          Username: email.toLowerCase().trim(),
          UserAttributes: [
            {
              Name: 'email',
              Value: email.toLowerCase().trim()
            },
            {
              Name: 'email_verified',
              Value: 'true'
            },
            {
              Name: 'profile',
              Value: profile === 'Administrator' ? 'admin' : profile === 'Officer' ? 'officer' : profile === 'Lawyer' ? 'legal' : 'cartorio'
            }
          ]
        };

        if (setDefaultPassword) {
          params.TemporaryPassword = defaultTemporaryPassword;
        }

        cognitoidentityserviceprovider.adminCreateUser(params, function (err, data) {
          if (err) {
            console.log('ERRO createUserCognito', err, err.stack);
          } else {
            console.log('SUCCESS createUserCognito', data);
          }
        });
      });
    },

    resetPasswodCognito: (email: string, profile: string, setDefaultPassword: boolean) => {
      return new Promise<void>((resolve, reject) => {
        AWS.config.region = ConfigService.getConfig().awsConfig.cognitoRegion;
        AWS.config.credentials = new AWS.CognitoIdentityCredentials({
          IdentityPoolId: ConfigService.getConfig().awsConfig.userPoolIdentityPoolId
        });
        const cognitoidentityserviceprovider = new AWS.CognitoIdentityServiceProvider();

        cognitoidentityserviceprovider.config.region = ConfigService.getConfig().awsConfig.cognitoRegion;

        if (cognitoidentityserviceprovider.config.credentials) {
          cognitoidentityserviceprovider.config.credentials.accessKeyId = ConfigService.getConfig().awsConfig.userPoolWebClientId;
          cognitoidentityserviceprovider.config.credentials.secretAccessKey = '';
        }

        const paramsUpdate: AdminUpdateUserAttributesRequest = {
          UserPoolId: ConfigService.getConfig().awsConfig.cognitoUserPoolId,
          Username: email.toLowerCase().trim(),
          UserAttributes: [
            {
              Name: 'email',
              Value: email.toLowerCase().trim()
            },
            {
              Name: 'email_verified',
              Value: 'true'
            },
            {
              Name: 'profile',
              Value: profile === 'Administrator' ? 'admin' : profile === 'Officer' ? 'officer' : profile === 'Lawyer' ? 'legal' : 'cartorio'
            }
          ]
        };
        cognitoidentityserviceprovider.adminUpdateUserAttributes(paramsUpdate, function (errUpdate, dataUpdate) {
          if (errUpdate) {
            console.log("ERRO resetPasswodCognito", errUpdate, errUpdate.stack);
          } else {
            const params: AdminCreateUserRequest = {
              Username: email.toLowerCase().trim(),
              UserPoolId: ConfigService.getConfig().awsConfig.cognitoUserPoolId,
              MessageAction: "RESEND",
              DesiredDeliveryMediums: ["EMAIL"],
            };
            if (setDefaultPassword) {
              params.TemporaryPassword = defaultTemporaryPassword;
            }
            cognitoidentityserviceprovider.adminCreateUser(params, function (err, data) {
              if (err?.code === 'UnsupportedUserStateException') {
                const setUserParams: AdminSetUserPasswordRequest = {
                  Username: email.toLowerCase().trim(),
                  UserPoolId: ConfigService.getConfig().awsConfig.cognitoUserPoolId,
                  Password: defaultTemporaryPassword,
                  Permanent: false
                };
                cognitoidentityserviceprovider.adminSetUserPassword(setUserParams, function (setPassErr, setPassData) {
                  if (setPassErr) {
                    console.log("ERRO adminSetUserPassword", err, err.stack);
                  }
                  else {
                    console.log("SUCCESS adminSetUserPassword", setPassData);
                  }
                })
              } else if (err) {
                console.log("ERRO adminCreateUser at reset", err, err.stack);
              } else {
                console.log("SUCCESS adminCreateUser at reset", data);
              }
            });
          }
        });
      });
    },

    setPasswordCognito: (email: string, password: string) => {
      return new Promise<void>((resolve, reject) => {
        AWS.config.region = ConfigService.getConfig().awsConfig.cognitoRegion;
        AWS.config.credentials = new AWS.CognitoIdentityCredentials({
          IdentityPoolId: ConfigService.getConfig().awsConfig.userPoolIdentityPoolId
        });

        (AWS.config.credentials as AWS.CognitoIdentityCredentials).refresh(function (error) {
          if (error) {
            console.log(error);
          } else {
            console.log('REFRESHED');
          }
        });
        const cognitoidentityserviceprovider = new AWS.CognitoIdentityServiceProvider();

        cognitoidentityserviceprovider.config.region = ConfigService.getConfig().awsConfig.cognitoRegion;

        if (cognitoidentityserviceprovider.config.credentials) {
          cognitoidentityserviceprovider.config.credentials.accessKeyId = ConfigService.getConfig().awsConfig.userPoolWebClientId;
          cognitoidentityserviceprovider.config.credentials.secretAccessKey = '';
        }

        const params = {
          Username: email,
          UserPoolId: ConfigService.getConfig().awsConfig.cognitoUserPoolId,
          Password: password
        };

        cognitoidentityserviceprovider.adminSetUserPassword(params, function (err, data) {
          if (err) {
            console.log('ERRO resetPasswodCognito', err, err.stack);
          } else {
            console.log('SUCCESS resetPasswodCognito', data);
          }
        });
      });
    },

    getUserCognito: (email: string) => {
      return new Promise<any>((resolve, reject) => {
        AWS.config.region = ConfigService.getConfig().awsConfig.cognitoRegion;
        AWS.config.credentials = new AWS.CognitoIdentityCredentials({
          IdentityPoolId: ConfigService.getConfig().awsConfig.userPoolIdentityPoolId
        });

        (AWS.config.credentials as AWS.CognitoIdentityCredentials).refresh(function (error) {
          if (error) {
            console.log(error);
          } else {
            console.log('REFRESHED');
          }
        });

        const cognitoidentityserviceprovider = new AWS.CognitoIdentityServiceProvider();

        cognitoidentityserviceprovider.config.region = ConfigService.getConfig().awsConfig.cognitoRegion;


        if (cognitoidentityserviceprovider.config.credentials) {
          cognitoidentityserviceprovider.config.credentials.accessKeyId = ConfigService.getConfig().awsConfig.userPoolWebClientId;
          cognitoidentityserviceprovider.config.credentials.secretAccessKey = '';
        }

        const params = {
          Username: email,
          UserPoolId: ConfigService.getConfig().awsConfig.cognitoUserPoolId
        };

        cognitoidentityserviceprovider.adminGetUser(params, function (err, data) {
          if (err) {
            console.log('ERRO getUserCognito', err, err.stack);
          } else {
            console.log('SUCCESS getUserCognito', data);
          }
        });
      });
    },

    sendMailToCognito: (email: string) => {
      if (provider.getUserCognito) {
        provider.getUserCognito(email)
          .then(() => provider.forgotPassword(email))
          .catch(() => provider.signUp(email, '@alterarprimeirasenha'));
      } else {
        console.error("getUserCognito is undefined");
      }
    },


    isLoggedIn: () => !!provider.currentUser || !!sessionStorage.getItem(SessionStorageEnum.session),
    hasCurentUser: () => !!provider.currentUser,

    currentEmail,
    currentUser
  };

  const trySetUserSession = async () => {
    try {
      await setUserSession("");
    } catch (error) {
      setMailAndUser(null);
    }
  };

  useEffect(() => {
    if (provider.isLoggedIn()) {
      trySetUserSession()
    }
  }, []);

  function setMailAndUser(arg) {
    setCurrentEmail(arg == null ? arg : arg.login);
    setCurrentUser(arg);
  }

  function setUserPermissions(user: IUserSecurityDto | IUserSecurityOfficerDto) {
    user.permissions = AuthPermissions(user);
  }

  return provider;
}
